export type Locale = "en" | "de";

export const LOCALES: Locale[] = ["en", "de"];
export const DEFAULT_LOCALE: Locale = "en";
export const LOCALE_STORAGE_KEY = "tenqora.locale";

export type ChecklistStepCopy = {
  title: string;
  detail: string;
};

export type Messages = {
  brand: {
    name: string;
    domain: string;
  };
  lang: {
    switchLabel: string;
    en: string;
    de: string;
  };
  common: {
    skipToContent: string;
    back: string;
    loading: string;
    retry: string;
    cancel: string;
    save: string;
    create: string;
    delete: string;
    confirm: string;
    signingIn: string;
    saving: string;
    creating: string;
    sending: string;
    unknown: string;
    noDescription: string;
    showPassword: string;
    hidePassword: string;
    showPasswordAction: string;
    hidePasswordAction: string;
    onlineRequired: string;
    pilotBanner: string;
    operatorBanner: string;
    toSite: string;
    privacy: string;
    terms: string;
    signIn: string;
    signOut: string;
    register: string;
    open: string;
  };
  nav: {
    overview: string;
    environment: string;
    machines: string;
    projects: string;
    tasks: string;
    connections: string;
    account: string;
    operator: string;
    admin: string;
    fleet: string;
    cabinet: string;
    workspaceRequests: string;
    primary: string;
    sections: string;
  };
  titles: {
    home: string;
    login: string;
    register: string;
    privacy: string;
    terms: string;
    overview: string;
    environment: string;
    environmentSave: string;
    machines: string;
    projects: string;
    project: string;
    workspaceRequests: string;
    tasks: string;
    task: string;
    lease: string;
    operation: string;
    connections: string;
    account: string;
    operator: string;
    admin: string;
    operatorWorkspaceRequests: string;
    notFound: string;
  };
  landing: {
    headline: string;
    lead: string;
    ctaStart: string;
    ctaExplore: string;
    navSections: string;
    navWorkflow: string;
    navPreview: string;
    navCapacity: string;
    pilotNote: string;
    artLabel: string;
    artAlt: string;
    workflowTitle: string;
    workflowLead: string;
    workflow1Title: string;
    workflow1Body: string;
    workflow2Title: string;
    workflow2Body: string;
    workflow3Title: string;
    workflow3Body: string;
    previewTitle: string;
    previewLead: string;
    previewBadge: string;
    previewLane1Title: string;
    previewLane1Body: string;
    previewLane2Title: string;
    previewLane2Body: string;
    previewLane3Title: string;
    previewLane3Body: string;
    capacityTitle: string;
    capacityLead: string;
    capacityStorageTitle: string;
    capacityStorageBody: string;
    capacityArchiveTitle: string;
    capacityArchiveBody: string;
    capacityBackupTitle: string;
    capacityBackupBody: string;
    capacityAvailability: string;
    bottomTitle: string;
    bottomLead: string;
    footerCopy: string;
  };
  auth: {
    loginTitle: string;
    loginLead: string;
    registerTitle: string;
    registerLead: string;
    email: string;
    password: string;
    name: string;
    organization: string;
    emailRequired: string;
    passwordRequired: string;
    nameRequired: string;
    orgRequired: string;
    passwordMin: string;
    ackRequired: string;
    ackLabel: string;
    loginAction: string;
    registerAction: string;
    noAccount: string;
    hasAccount: string;
    loginFailed: string;
    registerFailed: string;
  };
  legal: {
    privacyTitle: string;
    privacyP1: string;
    privacyP2: string;
    privacyP3: string;
    termsTitle: string;
    termsP1: string;
    termsP2: string;
    termsP3: string;
    termsP4: string;
    termsP5: string;
  };
  notFound: {
    code: string;
    title: string;
    body: string;
    home: string;
  };
  states: {
    sessionEnded: string;
    sessionEndedBody: string;
    forbidden: string;
    forbiddenBody: string;
    offline: string;
    offlineBody: string;
    error: string;
    empty: string;
    loadFailed: string;
  };
  api: {
    invalidJson: string;
    sessionExpired: string;
    forbidden: string;
    offline: string;
    network: string;
    httpError: string;
  };
  dashboard: {
    title: string;
    lead: string;
    projects: string;
    tasks: string;
    activeLeases: string;
    recentTasks: string;
    recentStates: string;
    recentStatesNote: string;
    noTasks: string;
    noTasksBody: string;
    noProjects: string;
    noProjectsBody: string;
    openProjects: string;
  };
  machines: {
    title: string;
    lead: string;
    empty: string;
    emptyBody: string;
    demoName: string;
    requestDemo: string;
    endLease: string;
    leaseCard: string;
    requestTitle: string;
    requestConfirm: string;
    requestBody: string;
    endTitle: string;
    endConfirm: string;
    endBody: string;
    endAck: string;
    modelUnknown: string;
    leasePrefix: string;
    mode: string;
    lastSeen: string;
  };
  leases: {
    title: string;
    back: string;
    noSsh: string;
    endingTitle: string;
    endingBody: string;
    endAction: string;
    endTitle: string;
    endBody: string;
    endAck: string;
    notFound: string;
  };
  projects: {
    title: string;
    lead: string;
    newTitle: string;
    name: string;
    description: string;
    nameRequired: string;
    createFailed: string;
    empty: string;
    emptyBody: string;
    detailLead: string;
    newTask: string;
    taskTitle: string;
    instructions: string;
    kind: string;
    kindPlan: string;
    kindDiagnostics: string;
    titleRequired: string;
    instructionsRequired: string;
    diagnosticsOnlyOperator: string;
    createTask: string;
    createTaskFailed: string;
    connectionsTitle: string;
    connectionsLead: string;
    connectionsEmpty: string;
    connectionsEmptyBody: string;
    connectionsFailed: string;
    retryConnections: string;
    availableFailed: string;
    retryAvailable: string;
    capChecklist: string;
    capAi: string;
    capRepo: string;
    capAvailable: string;
    capUnavailable: string;
    verificationNote: string;
    attachTitle: string;
    attachSelect: string;
    attachPlaceholder: string;
    attachNone: string;
    attachNeedConnections: string;
    attachAllBound: string;
    attach: string;
    attaching: string;
    attachFailed: string;
    manageConnections: string;
    detach: string;
    detachTitle: string;
    detachBody: string;
    detachConfirm: string;
    detachFailed: string;
    setupTitle: string;
    setupLead: string;
    setupConnections: string;
    setupConnectionsReady: string;
    setupConnectionsNeeded: string;
    setupVerification: string;
    setupVerificationNone: string;
    setupVerificationSummary: string;
    setupChecklistTask: string;
    setupChecklistReady: string;
    setupChecklistUnavailable: string;
    setupAi: string;
    setupAiUnavailable: string;
    setupRepo: string;
    setupRepoUnavailable: string;
    setupMac: string;
    setupMacUnavailable: string;
    setupDemoHint: string;
    setupDemoLink: string;
    setupCreateTask: string;
    setupNextSteps: string;
    workspaceRequestLink: string;
    activityTitle: string;
    activityLead: string;
    activityWindow: string;
    activityWindow24h: string;
    activityWindow7d: string;
    activityObserved: string;
    activitySource: string;
    activityFailed: string;
    activityRetry: string;
    activityRefresh: string;
    activityRefreshing: string;
    activityTableToggle: string;
    activityScaleMax: string;
    activityUtcNote: string;
    activityTaskCounts: string;
    activityTotal: string;
    activityMacRequests: string;
    activitySeriesTitle: string;
    activitySeriesLead: string;
    activityCreated: string;
    activitySucceeded: string;
    activityFailedEvents: string;
    activityChartSummary: string;
    activityTableCaption: string;
    activityBucket: string;
    activityCapsNote: string;
    activityRecent: string;
    activityRecentEmpty: string;
    servicesTitle: string;
    servicesLead: string;
    servicesSource: string;
    servicesSourceProbe: string;
    servicesObserved: string;
    servicesRefresh: string;
    servicesRefreshing: string;
    servicesRetry: string;
    servicesFailed: string;
    servicesEmpty: string;
    servicesEmptyBody: string;
    servicesCapsNote: string;
    servicesUnknown: string;
    servicesLastObserved: string;
    servicesReplicas: string;
    servicesReplicasValue: string;
    servicesProbeResult: string;
    servicesProbeOk: string;
    servicesProbeFail: string;
    servicesProbeDuration: string;
    servicesProbeNote: string;
    servicesCollectionOk: string;
    servicesCollectionUnavailable: string;
    servicesNoSamples: string;
    servicesChartTitle: string;
    servicesScaleMax: string;
    servicesChartSummary: string;
    servicesChartRange: string;
    servicesTableToggle: string;
    servicesTableCaption: string;
    servicesColWhen: string;
    servicesColCollection: string;
    servicesReleasesTitle: string;
    servicesReleasesLead: string;
    servicesReleasesEmpty: string;
    servicesReleaseRevision: string;
    servicesReleaseProgressing: string;
    servicesReleaseAvailable: string;
    servicesReleaseFailed: string;
    alertsTitle: string;
    alertsLead: string;
    alertsChannelNote: string;
    alertsChannelReported: string;
    alertsObserved: string;
    alertsRefresh: string;
    alertsRefreshing: string;
    alertsRetry: string;
    alertsFailed: string;
    alertsEmpty: string;
    alertsEmptyBody: string;
    alertsDisabledNote: string;
    alertsEnabledLabel: string;
    alertsDisabledLabel: string;
    alertsEnable: string;
    alertsDisable: string;
    alertsPrefFailed: string;
    alertsReason: string;
    alertsReasonDegraded: string;
    alertsReasonUnavailable: string;
    alertsReasonStale: string;
    alertsStatusOpen: string;
    alertsStatusResolved: string;
    alertsOpened: string;
    alertsResolved: string;
    alertsAcknowledged: string;
    alertsAcknowledge: string;
    alertsAcknowledging: string;
    alertsAckFailed: string;
    alertsAckNote: string;
  };
  workspace: {
    title: string;
    lead: string;
    bounds: string;
    newTitle: string;
    profile: string;
    profileRequired: string;
    toolsLabel: string;
    memory: string;
    memoryHint: string;
    memoryOption: string;
    memoryRequired: string;
    storage: string;
    storageHint: string;
    storageRange: string;
    duration: string;
    durationHint: string;
    durationRange: string;
    notes: string;
    notesHint: string;
    notesMax: string;
    submit: string;
    submitFailed: string;
    conflictActive: string;
    listTitle: string;
    empty: string;
    emptyBody: string;
    loadFailed: string;
    retry: string;
    summary: string;
    reviewNoteLabel: string;
    cancel: string;
    cancelTitle: string;
    cancelBody: string;
    cancelConfirm: string;
    cancelFailed: string;
    cancelConflict: string;
    pageStatus: string;
    prevPage: string;
    nextPage: string;
    planShow: string;
    planHide: string;
    planTitle: string;
    planMode: string;
    planNoExecution: string;
    planLoadFailed: string;
    planRetry: string;
    planSourceLabel: string;
    planSourcePersisted: string;
    planSourcePreview: string;
    planSnapshotAt: string;
    planRequestState: string;
    planPrerequisites: string;
    planPrerequisitesNone: string;
    planSteps: string;
    planStepsNone: string;
    planCapabilities: string;
    planCapProvisioning: string;
    planCapAiExecution: string;
    planCapArchiveRestore: string;
    planCapDisabled: string;
    planExecutableLabel: string;
    planExecutableNo: string;
    planModeLabel: string;
  };
  tasks: {
    title: string;
    lead: string;
    empty: string;
    emptyBody: string;
    noResults: string;
    noResultsBody: string;
    searchLabel: string;
    searchPlaceholder: string;
    stateLabel: string;
    stateAll: string;
    projectLabel: string;
    projectAll: string;
    applyFilters: string;
    refresh: string;
    refreshing: string;
    refreshStale: string;
    refreshFailed: string;
    retryRefresh: string;
    loadFailed: string;
    projectsFailed: string;
    backToFirstPage: string;
    createViaProjects: string;
    openProject: string;
    pageStatus: string;
    prevPage: string;
    nextPage: string;
    back: string;
    instructions: string;
    result: string;
    noResult: string;
    timelineTitle: string;
    timelineScope: string;
    timelineEmpty: string;
    cancel: string;
    cancelling: string;
    cancelRequested: string;
    cancelFailed: string;
    notFound: string;
  };
  checklist: {
    heading: string;
    engineNote: string;
    summary: string;
    stepLabel: string;
    diagnosticsHeading: string;
    diagnosticsNote: string;
    fieldCpu: string;
    fieldMem: string;
    fieldDisk: string;
    fieldUptime: string;
    otherFields: string;
  };
  checklistSteps: ChecklistStepCopy[];
  operations: {
    title: string;
    lead: string;
    back: string;
    runningTitle: string;
    runningBody: string;
    failedTitle: string;
    canceledTitle: string;
    failedBody: string;
    succeededTitle: string;
    succeededBody: string;
    pollError: string;
    openLease: string;
    openTask: string;
    noResource: string;
    unknownResource: string;
    toMachines: string;
    objectLabel: string;
    waitingObject: string;
    opLabel: string;
  };
  account: {
    title: string;
    organization: string;
    capabilities: string;
    profileTitle: string;
    profileLead: string;
    name: string;
    orgRenameHint: string;
    orgRenameForbidden: string;
    nameRequired: string;
    orgRequired: string;
    profileSaved: string;
    profileFailed: string;
    saveProfile: string;
    emailReadonly: string;
    roleReadonly: string;
    changePassword: string;
    currentPassword: string;
    newPassword: string;
    currentRequired: string;
    newMin: string;
    changed: string;
    changeFailed: string;
    sessionsTitle: string;
    sessionsLead: string;
    sessionCurrent: string;
    sessionExpires: string;
    sessionCreated: string;
    revokeSession: string;
    revokeOthers: string;
    revokeOthersTitle: string;
    revokeOthersBody: string;
    revokeOthersConfirm: string;
    revokeConfirmTitle: string;
    revokeConfirmBody: string;
    sessionsEmpty: string;
    sessionsFailed: string;
    revokeFailed: string;
    revokedCount: string;
    audit: string;
    auditEmpty: string;
    auditUnavailable: string;
    auditUnknown: string;
    auditActions: Record<string, string>;
    roles: Record<string, string>;
    roleUnknown: string;
    connections: string;
    connectionsLead: string;
    openConnections: string;
    openEnvironment: string;
    pilotMeta: string;
  };
  environment: {
    title: string;
    lead: string;
    activityTitle: string;
    projects: string;
    queued: string;
    running: string;
    completed: string;
    connections: string;
    demoLeases: string;
    demoLeasesHint: string;
    resourcesTitle: string;
    resourcesEmpty: string;
    resourcesEmptyProvisioning: string;
    resourceIntro: string;
    resourceUndescribed: string;
    storageProvisioningOn: string;
    liveComputeOff: string;
    archiveRestoreNote: string;
    paymentNotConnected: string;
    catalogTitle: string;
    catalogLead: string;
    productStorage: string;
    productArchive: string;
    productBackup: string;
    unitGb: string;
    availabilityRequest: string;
    requestTitle: string;
    product: string;
    amount: string;
    amountHint: string;
    amountRequired: string;
    amountRange: string;
    notes: string;
    notesHint: string;
    submit: string;
    submitting: string;
    submitFailed: string;
    conflict: string;
    historyTitle: string;
    requestStates: string;
    requestStatesNote: string;
    historyEmpty: string;
    cancel: string;
    cancelTitle: string;
    cancelBody: string;
    cancelConfirm: string;
    cancelFailed: string;
    stateRequested: string;
    stateCanceled: string;
  };
  integrations: {
    title: string;
    lead: string;
    newTitle: string;
    name: string;
    provider: string;
    secret: string;
    secretHint: string;
    nameRequired: string;
    secretMin: string;
    secretMax: string;
    save: string;
    saveFailed: string;
    boundsTitle: string;
    bound1: string;
    bound2: string;
    bound3: string;
    bound4: string;
    empty: string;
    verify: string;
    verifying: string;
    delete: string;
    deleteTitle: string;
    deleteBody: string;
    deleteAck: string;
    deleteConfirm: string;
    deleteUnexpected: string;
    deleteFailed: string;
    verifyFailed: string;
    lastChecked: string;
    rotate: string;
    rotating: string;
    rotateSave: string;
    rotateHint: string;
    newSecret: string;
    rotateFailed: string;
    rotateConflict: string;
  };
  operator: {
    title: string;
    lead: string;
    users: string;
    tenants: string;
    tasks: string;
    machines: string;
    events: string;
    noEvents: string;
    maintenanceOn: string;
    maintenanceOff: string;
    reason: string;
    reasonRequired: string;
    confirmTitle: string;
    confirmBody: string;
    confirmAction: string;
    actionFailed: string;
    serial: string;
  };
  admin: {
    title: string;
    lead: string;
    banner: string;
    network: string;
    rangesNote: string;
    park: string;
    fleet: string;
    organization: string;
    task: string;
    environment: string;
    machine: string;
    sourceCabinet: string;
    sourceTenants: string;
    sourceTasks: string;
    sourceUsers: string;
    sourceMachines: string;
    installedMemory: string;
    installedStorage: string;
    fieldNull: string;
    missingField: string;
    notASeries: string;
    loadChart: string;
    memoryUsedChart: string;
    memoryFreeChart: string;
    fieldLoad: string;
    fieldMemoryUsed: string;
    fieldMemoryFree: string;
    fieldDiskFree: string;
    fieldCpu: string;
    alsoMissing: string;
    usersNote: string;
    counts: string;
    audit: string;
    noMachines: string;
    reviewLead: string;
    enrollTitle: string;
    enrollLead: string;
    enrollName: string;
    serial: string;
    enrollPurpose: string;
    purposeTest: string;
    purposeRental: string;
    enrollSubmit: string;
    enrollFailed: string;
    tokenOnce: string;
    notConnectedTitle: string;
    notConnectedLead: string;
    notConnected: string;
    save: string;
    restore: string;
    export: string;
    delete: string;
    payment: string;
    icloud: string;
    assignment: string;
    mail: string;
    push: string;
    roles: string;
    settings: string;
    refusedTitle: string;
    refusedBody: string;
  };
  operatorWorkspace: {
    title: string;
    lead: string;
    bounds: string;
    queueTitle: string;
    queueLead: string;
    openQueue: string;
    stateFilter: string;
    stateAll: string;
    empty: string;
    emptyBody: string;
    markReviewing: string;
    decline: string;
    reviewTitle: string;
    declineTitle: string;
    reviewConfirm: string;
    declineConfirm: string;
    reviewNote: string;
    noteRequired: string;
    actionState: string;
    customerVisibleNote: string;
    conflict: string;
    actionFailed: string;
  };
  environmentSave: {
    bannerMark: string;
    banner: string;
    lead: string;
    back: string;
    variantLabel: string;
    phaseLabel: string;
    railNote: string;
    kicker: string;
    notConnected: string;
    variants: {
      ledger: { name: string; blurb: string };
      stage: { name: string; blurb: string };
      manifest: { name: string; blurb: string };
      letter: { name: string; blurb: string };
      rule: { name: string; blurb: string };
      index: { name: string; blurb: string };
      poster: { name: string; blurb: string };
    };
    studies: {
      earlier: string;
      newer: string;
      notFiled: string;
      letterMark: string;
      letterDate: string;
      letterMoments: string;
      letterScopeTitle: string;
      letterRecordTitle: string;
      letterTermTitle: string;
      letterScope: string;
      letterRecord: string;
      letterTerm: string;
      ruleFoot: string;
      ruleHold: string;
      ruleClosed: string;
      indexMoment: string;
      indexRetention: string;
      indexIcloud: string;
      indexDelete: string;
      indexImage: string;
      indexOrder: string;
      indexOrderValue: string;
      indexOrderGloss: string;
      indexNow: string;
      indexMoments: string;
      indexScope: string;
      indexTerm: string;
      indexClosed: string;
      indexClosedNote: string;
      indexQuotaValue: string;
      indexPayValue: string;
      indexSaveValue: string;
      indexRestoreValue: string;
      indexExportValue: string;
      indexDeleteValue: string;
      indexMac: string;
      indexMacValue: string;
      indexMacLive: string;
      indexMacLiveGloss: string;
      indexMacDemo: string;
      indexMacDemoGloss: string;
      indexChecks: string;
      indexChecksValue: string;
      indexChecksIdle: string;
      indexStateOff: string;
      indexStateOn: string;
      indexArchiveRequests: string;
      indexOpenCount: string;
      indexArchiveGloss: string;
      indexExpiry: string;
      indexExpiryValue: string;
      indexExpiryGloss: string;
      indexPayFlag: string;
      indexPayFlagGloss: string;
      indexSaveReported: string;
      indexSample: string;
      indexNone: string;
      indexNotChecked: string;
      indexIncludedValue: string;
      indexExcludedValue: string;
      indexCloudValue: string;
      indexLostValue: string;
      indexMonths: string;
      indexPrice: string;
      indexPriceValue: string;
      indexPayment: string;
      indexNotSignedIn: string;
      indexNotPromised: string;
      indexState: {
        running: string;
        saving: string;
        archived: string;
        restoring: string;
        attention: string;
      };
      posterVoid: string;
      posterFoot: string;
      posterScope: string;
      posterSample: string;
      posterTerm: string;
      posterClosed: string;
    };
    phases: {
      running: { name: string; body: string };
      saving: { name: string; body: string };
      archived: { name: string; body: string };
      restoring: { name: string; body: string };
      attention: { name: string; body: string };
    };
    lastSaveTitle: string;
    lastSaveNone: string;
    sizeTitle: string;
    sizeUnknown: string;
    retentionTitle: string;
    retentionBody: string;
    quotaTitle: string;
    quotaBody: string;
    compatibilityTitle: string;
    compatibilityBody: string;
    icloudTitle: string;
    icloudBody: string;
    macReady: string;
    progressSteps: string;
    pointsTitle: string;
    pointSample: string;
    pointUnverified: string;
    included: string;
    excluded: string;
    cloudOnly: string;
    nonRestorable: string;
    includedItems: string[];
    excludedItems: string[];
    cloudItems: string[];
    nonRestorableItems: string[];
    saveRelease: string;
    restore: string;
    exportAction: string;
    deleteAction: string;
    deleteConsequence: string;
    bootNote: string;
  };
  status: Record<string, string>;
  demo: {
    chip: string;
    liveChip: string;
  };
};
